import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';
import { downloadExport } from '@/services/school.api';
import type { ExportFormat } from '@/services/school.api';

// ───── Types ────────────────────────────────────────────────────────────────
// Mirrors the migrated `fee_demands`/`fee_payments`/`fee_structures` tables.
// Money is DECIMAL RUPEES (numeric(12,2) on the backend) — NOT paise, a
// deliberate deviation documented in schema-provisioner.service.ts. Every
// amount here is a plain number of rupees; never x100/รท100 it.

export interface FeeDefaultYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export type FeePaymentStatus = 'all' | 'paid' | 'unpaid';

export interface FeeReportParams {
  page?: number;
  limit?: number;
  search?: string;
  /** A year UUID, or 'all' (also the default) to aggregate every year — including undated demands. */
  academicYearId?: string;
  classId?: string;
  sectionId?: string;
  paymentStatus?: FeePaymentStatus;
}

export interface FeeReportStudentRow {
  studentId: string;
  admissionNo: string;
  studentName: string;
  className: string;
  sectionName: string;
  demanded: number;
  paid: number;
  pending: number;
}

export interface FeeReportResponse {
  academicYearId: string | null;
  totalDemanded: number;
  totalCollected: number;
  totalPending: number;
  students: PaginatedResult<FeeReportStudentRow>;
}

// ───── Screen A (Adjustment, DMD) / Screen B (Demands, NOR) types ──────────

export interface FeeDemandListParams {
  page?: number;
  limit?: number;
  search?: string;
  academicYearId?: string;
  classId?: string;
  sectionId?: string;
  feeStructureId?: string;
}

export interface FeeDemandRow {
  id: string;
  studentId: string;
  admissionNo: string;
  studentName: string;
  classId: string | null;
  className: string;
  sectionId: string | null;
  sectionName: string;
  feeStructureId: string | null;
  feeStructureName: string;
  academicYearId: string;
  academicYearName: string;
  dueDate: string | null;
  actualAmount: number;
  discountAmount: number;
  discountReason: string | null;
  dmdAmount: number;
  adjustedAmount: number;
  pending: number;
}

// ───── Screen A (Adjustment) — grouped list ─────────────────────────────
// One row per (student, fee_structure, academic_year), matching the old
// system's GROUP BY, with every underlying fee_demands row (one real
// installment) nested inside. `actualAmount` (gross) vs `balance`
// (net-of-discount) follow the same split as the Fee Report — see
// fees.service.ts's FeeAdjustmentGroupRowDto doc comment.

export interface FeeAdjustmentInstallment {
  id: string;
  installmentName: string | null;
  dueDate: string | null;
  actualAmount: number;
  paidAmount: number;
  balance: number;
  discountAmount: number;
  discountReason: string | null;
}

export interface FeeAdjustmentGroupRow {
  studentId: string;
  admissionNo: string;
  studentName: string;
  className: string;
  sectionName: string;
  feeStructureId: string | null;
  feeStructureName: string;
  academicYearId: string;
  academicYearName: string;
  actualAmount: number;
  paidAmount: number;
  balance: number;
  installments: FeeAdjustmentInstallment[];
}

export interface RecordFeePaymentPayload {
  amount: number;
  date: string;
  mode: string;
  reference?: string;
  image?: string;
}

export interface ApplyFeeDiscountPayload {
  amount: number;
  reason: string;
}

export interface CreateFeeDemandPayload {
  studentId: string;
  feeStructureId: string;
  academicYearId?: string;
  amount: number;
  discountAmount?: number;
  dueDate?: string;
  paymentDate?: string;
  paymentMode?: string;
  paymentReference?: string;
  paymentImage?: string;
  paidAmount?: number;
}

export type UpdateFeeDemandPayload = Partial<CreateFeeDemandPayload>;

export interface FeeStudentPickerRow {
  id: string;
  admissionNo: string;
  name: string;
  className: string;
  sectionName: string;
}

export interface FeeStructurePickerRow {
  id: string;
  name: string;
  className: string;
}

// ───── Create Noorani Fees ──────────────────────────────────────────────

export interface CreateNooraniFeeItem {
  feeType: string;
  amount: number;
}

export interface CreateNooraniFeePayload {
  prefixName: string;
  groupIds: string[];
  items: CreateNooraniFeeItem[];
  dueDate: string;
  installmentEnabled: boolean;
  academicYearId?: string;
}

export interface CreateNooraniFeeResult {
  feeStructureIds: string[];
  studentCount: number;
  itemCount: number;
  totalDemandsCreated: number;
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body = r.data as { data?: T };
  return body && typeof body === 'object' && 'data' in body ? (body.data as T) : (r.data as T);
}

export const FeesApi = {
  defaultYear: async (): Promise<FeeDefaultYear | null> => unwrap(await api.get('/fees/report/default-year')),

  report: async (params: FeeReportParams): Promise<FeeReportResponse> =>
    unwrap(await api.get('/fees/report', { params })),

  classSummaryPdf: (format: ExportFormat, params: FeeReportParams) =>
    downloadExport('/fees/report/class-summary-pdf', 'fee-class-summary', format, params as Record<string, unknown>),

  paidUnpaidPdf: (format: ExportFormat, params: FeeReportParams) =>
    downloadExport('/fees/report/paid-unpaid-pdf', 'fee-paid-unpaid-summary', format, params as Record<string, unknown>),

  // ── Screen A: Adjustment (bulk DMD demands, grouped by student+fee+year) ──
  adjustmentList: async (params: FeeDemandListParams): Promise<PaginatedResult<FeeAdjustmentGroupRow>> =>
    unwrap(await api.get('/fees/adjustment', { params })),

  recordPayment: async (demandId: string, payload: RecordFeePaymentPayload): Promise<FeeDemandRow> =>
    unwrap(await api.patch(`/fees/adjustment/${demandId}/payment`, payload)),

  applyDiscount: async (demandId: string, payload: ApplyFeeDiscountPayload): Promise<FeeDemandRow> =>
    unwrap(await api.patch(`/fees/adjustment/${demandId}/discount`, payload)),

  adjustmentClassSummaryPdf: (format: ExportFormat, params: FeeDemandListParams) =>
    downloadExport('/fees/adjustment/class-summary-pdf', 'fee-adjustment-class-summary', format, params as Record<string, unknown>),

  // ── Screen B: Demands (manual NOR demands) ──
  demandsList: async (params: FeeDemandListParams): Promise<PaginatedResult<FeeDemandRow>> =>
    unwrap(await api.get('/fees/demands', { params })),

  getDemand: async (id: string): Promise<FeeDemandRow> => unwrap(await api.get(`/fees/demands/${id}`)),

  createDemand: async (payload: CreateFeeDemandPayload): Promise<FeeDemandRow> =>
    unwrap(await api.post('/fees/demands', payload)),

  updateDemand: async (id: string, payload: UpdateFeeDemandPayload): Promise<FeeDemandRow> =>
    unwrap(await api.patch(`/fees/demands/${id}`, payload)),

  deleteDemand: async (id: string): Promise<void> => unwrap(await api.delete(`/fees/demands/${id}`)),

  // ── shared pickers ──
  studentsPicker: async (params: { classId?: string; sectionId?: string; search?: string }): Promise<FeeStudentPickerRow[]> =>
    unwrap(await api.get('/fees/students', { params })),

  feeStructuresPicker: async (classId?: string): Promise<FeeStructurePickerRow[]> =>
    unwrap(await api.get('/fees/fee-structures', { params: classId ? { classId } : undefined })),

  // ── Noorani Fees — a filtered VIEW over the same fee_demands pipeline
  // (fee_structures.name ILIKE '%noorani%'), reusing the same list/report
  // shapes and the same payment/discount action logic as Screen A. ──
  nooraniList: async (params: FeeDemandListParams): Promise<PaginatedResult<FeeAdjustmentGroupRow>> =>
    unwrap(await api.get('/fees/noorani', { params })),

  nooraniRecordPayment: async (demandId: string, payload: RecordFeePaymentPayload): Promise<FeeDemandRow> =>
    unwrap(await api.patch(`/fees/noorani/${demandId}/payment`, payload)),

  nooraniApplyDiscount: async (demandId: string, payload: ApplyFeeDiscountPayload): Promise<FeeDemandRow> =>
    unwrap(await api.patch(`/fees/noorani/${demandId}/discount`, payload)),

  nooraniReport: async (params: FeeReportParams): Promise<FeeReportResponse> =>
    unwrap(await api.get('/fees/noorani/report', { params })),

  nooraniClassSummaryPdf: (format: ExportFormat, params: FeeReportParams) =>
    downloadExport('/fees/noorani/report/class-summary-pdf', 'noorani-fee-class-summary', format, params as Record<string, unknown>),

  nooraniPaidUnpaidPdf: (format: ExportFormat, params: FeeReportParams) =>
    downloadExport('/fees/noorani/report/paid-unpaid-pdf', 'noorani-fee-paid-unpaid-summary', format, params as Record<string, unknown>),

  nooraniCreate: async (payload: CreateNooraniFeePayload): Promise<CreateNooraniFeeResult> =>
    unwrap(await api.post('/fees/noorani', payload)),

  nooraniFeeTypeSuggestions: async (): Promise<string[]> => unwrap(await api.get('/fees/noorani/fee-types')),
};

// ───── Org tier — Manage Fees / Noorani Fees / Noorani Fee Report, reachable
// across any school in scope via an explicit schoolId, instead of switching
// the whole session into that school first. Mirrors OrgFeeTypesApi's shape. ─

export interface OrgFeeSchool {
  id: string;
  name: string;
}

export const OrgFeesApi = {
  schools: async (): Promise<OrgFeeSchool[]> => unwrap(await api.get('/org/fees/schools')),

  academicYears: async (schoolId: string) =>
    unwrap<PaginatedResult<{ id: string; name: string }>>(await api.get('/org/fees/academic-years', { params: { schoolId } })),

  classes: async (schoolId: string) =>
    unwrap<PaginatedResult<{ id: string; name: string; sections: { id: string; name: string }[] }>>(
      await api.get('/org/fees/classes', { params: { schoolId } }),
    ),

  defaultYear: async (schoolId: string): Promise<FeeDefaultYear | null> =>
    unwrap(await api.get('/org/fees/report/default-year', { params: { schoolId } })),

  report: async (schoolId: string, params: FeeReportParams): Promise<FeeReportResponse> =>
    unwrap(await api.get('/org/fees/report', { params: { ...params, schoolId } })),

  classSummaryPdf: (format: ExportFormat, schoolId: string, params: FeeReportParams) =>
    downloadExport('/org/fees/report/class-summary-pdf', 'fee-class-summary', format, { ...params, schoolId } as Record<string, unknown>),

  paidUnpaidPdf: (format: ExportFormat, schoolId: string, params: FeeReportParams) =>
    downloadExport('/org/fees/report/paid-unpaid-pdf', 'fee-paid-unpaid-summary', format, { ...params, schoolId } as Record<string, unknown>),

  nooraniList: async (schoolId: string, params: FeeDemandListParams): Promise<PaginatedResult<FeeAdjustmentGroupRow>> =>
    unwrap(await api.get('/org/fees/noorani', { params: { ...params, schoolId } })),

  nooraniRecordPayment: async (schoolId: string, demandId: string, payload: RecordFeePaymentPayload): Promise<FeeDemandRow> =>
    unwrap(await api.patch(`/org/fees/noorani/${demandId}/payment`, { ...payload, schoolId })),

  nooraniApplyDiscount: async (schoolId: string, demandId: string, payload: ApplyFeeDiscountPayload): Promise<FeeDemandRow> =>
    unwrap(await api.patch(`/org/fees/noorani/${demandId}/discount`, { ...payload, schoolId })),

  nooraniReport: async (schoolId: string, params: FeeReportParams): Promise<FeeReportResponse> =>
    unwrap(await api.get('/org/fees/noorani/report', { params: { ...params, schoolId } })),

  nooraniClassSummaryPdf: (format: ExportFormat, schoolId: string, params: FeeReportParams) =>
    downloadExport('/org/fees/noorani/report/class-summary-pdf', 'noorani-fee-class-summary', format, { ...params, schoolId } as Record<string, unknown>),

  nooraniPaidUnpaidPdf: (format: ExportFormat, schoolId: string, params: FeeReportParams) =>
    downloadExport('/org/fees/noorani/report/paid-unpaid-pdf', 'noorani-fee-paid-unpaid-summary', format, { ...params, schoolId } as Record<string, unknown>),

  nooraniCreate: async (schoolId: string, payload: CreateNooraniFeePayload): Promise<CreateNooraniFeeResult> =>
    unwrap(await api.post('/org/fees/noorani', { ...payload, schoolId })),

  nooraniFeeTypeSuggestions: async (schoolId: string): Promise<string[]> =>
    unwrap(await api.get('/org/fees/noorani/fee-types', { params: { schoolId } })),
};
